import React, { useState, useRef, useEffect } from 'react'
import styles from './house.module.scss'
import { HouseImage } from './house-image'
import { HouseMarker } from './house-marker'
import posed from 'react-pose'
import panzoom, { PanZoom } from 'panzoom'
import { Button } from '../button'
import { HouseMarkerData } from '../../interfaces'
import { useMatchMedia } from '../../hooks/use-match-media'
import { ReactComponent as RotateIcon } from '../../svg/rotate-device.svg'
const useResizeAware = require('react-resize-aware')

interface Props {
  markers: HouseMarkerData[]
}

const AnimatedHouse = posed(HouseImage)({
  visible: { opacity: 1 },
  hidden: { opacity: 0 }
})

const staggerDuration = 150
const AnimatedMarker = posed(HouseMarker)({
  visible: {
    opacity: 1,
    y: 0,
    delay: ({ index }: { index: number }) => (index + 1) * staggerDuration
  },
  hidden: {
    opacity: 0,
    y: -100
  }
})

export const House: React.FC<Props> = ({ markers }) => {
  // State
  const [outerHouseVisible, setOuterHouseVisible] = useState(true)
  const isMobile = useMatchMedia(`(max-width: ${styles.medium})`)
  const isPortrait = useMatchMedia(`(orientation: portrait)`)
  const [zoomContainerListener, zoomContainerSizes] = useResizeAware()

  // Setup zoom container
  const zoomContainer = useRef<HTMLDivElement>(null)
  const pz = useRef<PanZoom | null>(null)

  useEffect(() => {
    if (zoomContainer && zoomContainer.current && isMobile) {
      pz.current = panzoom(zoomContainer.current, {
        maxZoom: 2,
        minZoom: 1.4,
        smoothScroll: false,
        bounds: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        },
        // Allow tapping on buttons
        onTouch (e) {
          const target = e.target as HTMLElement
          if (target.tagName !== 'BUTTON' && target.tagName !== 'A') {
            e.preventDefault()
          }
        }
      })

      // Update the value of the scale custom property
      pz.current.on('zoom', (e: PanZoom) => {
        if (zoomContainer.current) {
          zoomContainer.current.style.setProperty(
            '--scale',
            `${e.getTransform().scale}`
          )
        }
      })

      // Snap to the max translate value when panning past the end of the "bounds"
      pz.current.on('panend', (e: PanZoom) => {
        if (zoomContainer.current) {
          const { x } = e.getTransform()
          const maxTranslate =
            zoomContainer.current.getBoundingClientRect().width -
            zoomContainer.current.clientWidth

          if (Math.abs(x) >= maxTranslate) {
            e.moveBy(-(x + maxTranslate), 0, true)
          }
        }
      })
    }

    // Destroy on desktop
    if (pz.current && !isMobile && zoomContainer.current) {
      pz.current.dispose()
      zoomContainer.current.style.setProperty('transform', 'none')
    }
  }, [isMobile])

  // Zoom to center of the image
  useEffect(() => {
    if (isMobile && pz.current) {
      const { width, height } = zoomContainerSizes

      if (width > 0) {
        pz.current.zoomAbs(width / 2, height / 2, 1.4)
      }
    }
  }, [isMobile, zoomContainerSizes])

  useEffect(() => {
    // Fade out outer house image
    const timeOut = setTimeout(() => {
      setOuterHouseVisible(false)
    }, 2000)

    return () => {
      clearTimeout(timeOut)
    }
  }, [])

  // Only show the house image on desktop or portrait mobile
  const houseVisible = !isMobile || (isMobile && isPortrait)

  return (
    <div className={styles.grid}>
      {!houseVisible && (
        <div className={styles.rotateMessage}>
          <div>
            <RotateIcon className={styles.rotateIcon} />
            Please rotate your device to interact with this tool.
          </div>
        </div>
      )}
      <div className={styles.content}>
        <h1>How water is used in your home?</h1>
      </div>
      <div className={styles.mask}>
        <div className={styles.container} ref={zoomContainer}>
          {isMobile && zoomContainerListener}
          {houseVisible && (
            <>
              {markers.map(({ title, content, coords, link }, index) => (
                <AnimatedMarker
                  key={index}
                  index={index}
                  coords={coords}
                  title={title}
                  content={content}
                  link={link}
                  pose={outerHouseVisible ? 'hidden' : 'visible'}
                />
              ))}
              <HouseImage imageName='house-inside' />
              <AnimatedHouse
                imageName='house-outside'
                pose={outerHouseVisible ? 'visible' : 'hidden'}
              />
            </>
          )}
        </div>
      </div>
      <div className={`${styles.cta} ${styles.ctaAlt}`}>
        <div className={styles.ctaContent}>
          <h3>What can I do to save water?</h3>
          <p>
            See how waterwise your home really is by comparing your use with
            similar households.
          </p>
          <Button href='https://www.watercorporation.com.au/Bill-and-account'>
            Compare your use
          </Button>
        </div>
      </div>
      <div className={styles.cta}>
        <Button href='https://www.watercorporation.com.au/our-water/wastewater'>Where does your water go?</Button>
      </div>
    </div>
  )
}
